import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { clsx } from 'clsx';
import moment from 'moment';
import { ISO_DATE_FORMAT } from 'src/shared/constants';

const ArrowLeft = () => {
  return (
    <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.36723 7.5L7.83391 0.958523C7.88715 0.905806 7.92924 0.84322 7.95775 0.774386C7.98625 0.705552 8.00061 0.631837 7.99998 0.557506C7.99935 0.483175 7.98376 0.409704 7.95409 0.341346C7.92443 0.272987 7.88129 0.211098 7.82716 0.15926C7.77304 0.107422 7.70901 0.0666637 7.63878 0.0393444C7.56854 0.012025 7.4935 -0.0013131 7.418 0.000101867C7.3425 0.00151683 7.26803 0.0176568 7.1989 0.0475878C7.12978 0.0775187 7.06737 0.120646 7.01529 0.174475L0.161773 7.10798C0.05804 7.21293 0 7.35357 0 7.5C0 7.64643 0.05804 7.78707 0.161773 7.89202L7.01529 14.8255C7.06737 14.8794 7.12978 14.9225 7.1989 14.9524C7.26803 14.9823 7.3425 14.9985 7.418 14.9999C7.4935 15.0013 7.56854 14.988 7.63878 14.9607C7.70901 14.9333 7.77304 14.8926 7.82716 14.8407C7.88129 14.7889 7.92443 14.727 7.95409 14.6587C7.98376 14.5903 7.99935 14.5168 7.99998 14.4425C8.00061 14.3682 7.98625 14.2944 7.95775 14.2256C7.92924 14.1568 7.88715 14.0942 7.83391 14.0415L1.36723 7.5Z"
        fill="#05050D"
      />
    </svg>
  );
};
const ArrowRight = () => {
  return (
    <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.5292 7.5L0.163868 14.0415C0.111461 14.0942 0.0700293 14.1568 0.0419698 14.2256C0.0139104 14.2944 -0.000219767 14.3682 0.000397731 14.4425C0.00101523 14.5168 0.0163679 14.5903 0.0455671 14.6587C0.0747662 14.727 0.117232 14.7889 0.170507 14.8407C0.223783 14.8926 0.286809 14.9333 0.355941 14.9607C0.425072 14.988 0.498936 15.0013 0.573256 14.9999C0.647576 14.9985 0.720877 14.9823 0.788918 14.9524C0.85696 14.9225 0.918389 14.8794 0.969652 14.8255L7.71576 7.89202C7.81787 7.78707 7.875 7.64643 7.875 7.5C7.875 7.35357 7.81787 7.21293 7.71576 7.10798L0.969653 0.174473C0.91839 0.120645 0.856961 0.0775188 0.78892 0.0475877C0.720878 0.0176567 0.647577 0.00151666 0.573257 0.000101405C0.498937 -0.0013129 0.425073 0.0120242 0.355942 0.0393441C0.286811 0.0666631 0.223784 0.10742 0.170509 0.159259C0.117233 0.211097 0.0747674 0.272987 0.0455683 0.341345C0.0163692 0.409704 0.00101646 0.483175 0.000398945 0.557505C-0.000218566 0.631836 0.0139115 0.705551 0.041971 0.774386C0.0700305 0.84322 0.111462 0.905806 0.163869 0.958522L6.5292 7.5Z"
        fill="#05050D"
      />
    </svg>
  );
};

function getWeekRange(baseDate) {
  const today = moment();
  const start = baseDate.clone().startOf('isoWeek');
  const endOfWeek = baseDate.clone().endOf('isoWeek');

  const end = baseDate.isSame(today, 'isoWeek') ? moment.min(today, endOfWeek) : endOfWeek;

  return {
    start_time: start.format(ISO_DATE_FORMAT),
    end_time: end.format(ISO_DATE_FORMAT),
  };
}

export const WeeklyDateSelect = ({ setDateRange }) => {
  const today = moment();
  const [range, setRange] = useState(() => ({
    label: 'Custom',
    ...getWeekRange(today),
  }));

  const goPrevWeek = () => {
    setRange((prev) => {
      const prevWeekDate = moment(prev.start_time).subtract(1, 'week');
      return {
        label: 'Custom',
        ...getWeekRange(prevWeekDate),
      };
    });
  };

  const goNextWeek = () => {
    setRange((prev) => {
      const nextWeekDate = moment(prev.start_time).add(1, 'week');

      if (nextWeekDate.isAfter(today, 'isoWeek')) {
        return prev;
      }
      return {
        label: 'Custom',
        ...getWeekRange(nextWeekDate),
      };
    });
  };

  useEffect(() => {
    setDateRange?.(range);
  }, [range, setDateRange]);

  const isNextDisabled = moment(range.start_time).add(1, 'week').isAfter(today, 'isoWeek');

  const formatRange = (startISO, endISO) => {
    return `${moment(startISO).format('MMM D')} – ${moment(endISO).format('MMM D')}`;
  };

  return (
    <div className="flex items-center gap-3.5 overflow-auto">
      <div className="flex items-center gap-1.5">
        <Button variant="ghost" onClick={goPrevWeek}>
          <ArrowLeft />
        </Button>

        <div className="border rounded-sm-5 border-[#05050D0D] flex items-center justify-center px-3 py-2">
          <p className="text-xs opacity-70 font-semibold">
            {formatRange(range.start_time, range.end_time)}
          </p>
        </div>

        <Button
          variant="ghost"
          onClick={goNextWeek}
          disabled={isNextDisabled}
          className={clsx(isNextDisabled && '!opacity-15')}
        >
          <ArrowRight />
        </Button>
      </div>
    </div>
  );
};
